"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface DocCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  display_order: number;
}

interface DocArticle {
  id: string;
  category_id: string;
  title: string;
  slug: string;
  is_published: boolean;
  display_order: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  'getting-started': 'RocketLaunchIcon',
  'virtual-machines': 'ServerIcon',
  'storage': 'CircleStackIcon',
  'networking': 'GlobeAltIcon',
  'api-reference': 'CodeBracketIcon',
  'security': 'ShieldCheckIcon',
  'databases': 'TableCellsIcon',
  'account': 'UserCircleIcon',
};

interface DocsSidebarProps {
  categories: DocCategory[];
  articles: DocArticle[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}

export default function DocsSidebar({ categories, articles, activeSlug, onSelect }: DocsSidebarProps) {
  const [openCategories, setOpenCategories] = useState<string[]>(
    categories.slice(0, 2).map((c) => c.id)
  );

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const getIcon = (slug: string) => CATEGORY_ICONS[slug] || 'FolderIcon';

  return (
    <aside className="w-full lg:w-72 flex-shrink-0">
      <div className="glass rounded-2xl p-4 border border-[rgba(79,124,255,0.12)] sticky top-28">
        <div className="space-y-1">
          {categories.map((cat) => {
            const catArticles = articles.filter(
              (a) => a.category_id === cat.id && a.is_published
            );
            const isOpen = openCategories.includes(cat.id);
            return (
              <div key={cat.id}>
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-[#8BA3CC] hover:text-white hover:bg-[rgba(79,124,255,0.08)] transition-all"
                >
                  <div className="flex items-center gap-2.5">
                    <Icon name={getIcon(cat.slug) as any} size={16} className="text-[#4F7CFF]" />
                    {cat.name}
                  </div>
                  <Icon
                    name="ChevronRightIcon"
                    size={14}
                    className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
                {isOpen && catArticles.length > 0 && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-[rgba(79,124,255,0.15)] pl-3">
                    {catArticles.map((article) => (
                      <button
                        key={article.id}
                        onClick={() => onSelect(article.slug)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          activeSlug === article.slug
                            ? "text-[#00D4FF] bg-[rgba(0,212,255,0.08)]"
                            : "text-[#4A6080] hover:text-[#8BA3CC] hover:bg-[rgba(79,124,255,0.05)]"
                        }`}
                      >
                        {article.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}